<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <button
      type="button"
      @click="open = !open"
      class="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
    >
      <span>
        <span class="block text-sm font-semibold text-gray-900 dark:text-white">How these numbers are calculated</span>
        <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">What counts, what does not, and where the figures have limits</span>
      </span>
      <svg class="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200" :class="open ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <div v-if="open" class="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-gray-700">
      <div class="overflow-x-auto">
        <table class="w-full text-[13px] mt-3">
          <thead>
            <tr class="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              <th class="py-2 pr-4 font-semibold">Figure</th>
              <th class="py-2 pr-4 font-semibold">Counts</th>
              <th class="py-2 font-semibold">Out of</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800 text-gray-600 dark:text-gray-300 align-top">
            <tr v-for="row in DEFINITIONS" :key="row.name">
              <td class="py-2.5 pr-4 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{{ row.name }}</td>
              <td class="py-2.5 pr-4">{{ row.numerator }}</td>
              <td class="py-2.5">{{ row.denominator }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ul class="mt-4 space-y-2 text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
        <li v-for="note in NOTES" :key="note" class="flex gap-2">
          <span class="text-gray-300 dark:text-gray-600 flex-shrink-0">•</span>
          <span>{{ note }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

// Collapsed by default: the casual reader is not taxed, the sceptical one is
// satisfied. A dashboard that hides its definitions is one people quietly stop
// trusting.
const open = ref(false)

const DEFINITIONS = [
  {
    name: 'Completed',
    numerator: 'Tasks you finished inside the selected period',
    denominator: 'Not a ratio',
  },
  {
    name: 'Completion rate',
    numerator: 'Tasks finished',
    denominator: 'Tasks due in the period, whether finished, cancelled or still open. Cancelled work counts against this rate and never towards it',
  },
  {
    name: 'On time',
    numerator: 'Finished on or before the due date',
    denominator: 'Tasks finished in the period that had a due date',
  },
  {
    name: 'Median turnaround',
    numerator: 'The middle value from created to completed',
    denominator: 'Tasks finished in the period. Median rather than average, so one long forgotten task cannot distort it',
  },
  {
    name: 'Overdue now',
    numerator: 'Open tasks already past their due date',
    denominator: 'Counted at this moment, not across the period',
  },
]

const NOTES = [
  'Tasks with no due date are left out of the completion and on time rates entirely, on both sides. A task with no date made no commitment, so it can be neither kept nor broken.',
  'Deleted tasks are gone for good, so these figures only describe work that still exists.',
  'Reopening a finished task clears its completion date, so it leaves the period it was originally finished in.',
  'Open work by status, overdue counts and the longest open list describe this moment rather than the selected period. They are labelled "as of now" wherever they appear.',
]
</script>
